<template>
	<div class="uni-directional-label-bg" aria-hidden="true">
		<!-- BASE: left cap (fixed proportions) -->
		<svg
			class="uni-directional-label-bg__baseLeft"
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 4 94"
			preserveAspectRatio="xMinYMid meet"
		>
			<!-- left bevel -->
			<path
				d="M4,4L0,0L0,94L4,90L4,4Z"
				:style="`fill: ${skins.get(skin)?.color1}; fill-rule: nonzero`"
			/>
			<!-- top-left strip -->
			<path
				d="M4,0L0,0L4,4L4,0Z"
				:style="`fill: ${skins.get(skin)?.color2}`"
			/>
			<!-- bottom-left strip -->
			<path
				d="M4,90L0,94L4,94L4,90Z"
				:style="`fill: ${skins.get(skin)?.color3}`"
			/>
		</svg>

		<!-- BASE: right stretch (only this part stretches in width) -->
		<svg
			class="uni-directional-label-bg__baseMid"
			xmlns="http://www.w3.org/2000/svg"
			viewBox="4 0 203 94"
			preserveAspectRatio="none"
		>
			<!-- bottom strip -->
			<path
				d="M207,90L4,90L4,94L207,94L207,90Z"
				:style="`fill: ${skins.get(skin)?.color3}`"
			/>
			<!-- main fill -->
			<rect
				x="4"
				y="4"
				width="203"
				height="86"
				:style="`fill: url(#_LinearBaseMid--${skin})`"
			/>
			<!-- top strip -->
			<path
				d="M207,0L4,0L4,4L207,4L207,0Z"
				:style="`fill: ${skins.get(skin)?.color2}`"
			/>

			<defs>
				<!-- stretchy gradient -->
				<linearGradient
					:id="`_LinearBaseMid--${skin}`"
					x1="0"
					y1="0"
					x2="1"
					y2="0"
					gradientUnits="userSpaceOnUse"
					gradientTransform="matrix(0,-94,94,0,135.16,94)"
				>
					<stop
						offset="0"
						:style="`stop-color: ${skins.get(skin)?.color5}; stop-opacity: 1`"
					/>
					<stop
						offset="1"
						:style="`stop-color: ${skins.get(skin)?.color6}; stop-opacity: 1`"
					/>
				</linearGradient>
			</defs>
		</svg>

		<!-- END (fixed proportions) -->
		<svg
			class="uni-directional-label-bg__end"
			xmlns="http://www.w3.org/2000/svg"
			viewBox="207 0 66 94"
			preserveAspectRatio="xMinYMid meet"
		>
			<path
				d="M207,94L210,94L208.67,89.872L207,89.872L207,94Z"
				:style="`fill: ${skins.get(skin)?.color3}`"
			/>
			<path
				d="M207,90L207,4L208.67,4L266.31,47L208.67,90L207,90Z"
				:style="`fill: url(#_LinearEnd--${skin})`"
			/>
			<path
				d="M208.67,90L210,94L273,47L266.31,47L208.67,90Z"
				:style="`fill: ${skins.get(skin)?.color3}`"
			/>
			<path
				d="M273,47L210,0L208.67,4L266.31,47L273,47Z"
				:style="`fill: ${skins.get(skin)?.color4}`"
			/>
			<path
				d="M207,0L210,0L208.67,4L207,4L207,0Z"
				:style="`fill: ${skins.get(skin)?.color2}`"
			/>

			<defs>
				<linearGradient
					:id="`_LinearEnd--${skin}`"
					x1="0"
					y1="0"
					x2="1"
					y2="0"
					gradientUnits="userSpaceOnUse"
					gradientTransform="matrix(0,-94,94,0,135.16,94)"
				>
					<stop
						offset="0"
						:style="`stop-color: ${skins.get(skin)?.color5}; stop-opacity: 1`"
					/>
					<stop
						offset="1"
						:style="`stop-color: ${skins.get(skin)?.color6}; stop-opacity: 1`"
					/>
				</linearGradient>
			</defs>
		</svg>
	</div>
</template>

<script lang="ts" setup>
import { UniDirectionalLabelSkinTypes } from "@/types/ui";

interface Props {
	skin: UniDirectionalLabelSkinTypes;
}

const { skin = UniDirectionalLabelSkinTypes.RED } = defineProps<Props>();

const skins = new Map<string, Record<string, string>>([
	[
		UniDirectionalLabelSkinTypes.RED,
		{
			color1: "#cb2933",
			color2: "#e22b38",
			color3: "#24090d",
			color4: "#ad0d19",
			color5: "#8b0710",
			color6: "#df101e",
		},
	],
	[
		UniDirectionalLabelSkinTypes.GREEN,
		{
			color1: "#139137",
			color2: "#3abd60",
			color3: "#063b15",
			color4: "#38aa58",
			color5: "#0d6c27",
			color6: "#19b345",
		},
	],
	[
		UniDirectionalLabelSkinTypes.GOLD,
		{
			color1: "#e2a326",
			color2: "#ffcc48",
			color3: "#4d2401",
			color4: "#e69936",
			color5: "#c55205",
			color6: "#ffc731",
		},
	],
]);
</script>

<style lang="scss" scoped>
.uni-directional-label-bg {
	display: flex;
	width: 100%;
	height: 100%;

	&::before {
		position: absolute;
		top: 0;
		left: 0;
		@include background(
			url($path-images + "backgrounds/textures/window-texture-bg.avif"),
			20rem,
			center,
			repeat
		);
		width: 100%;
		height: 100%;
		content: "";
		--arrow: 25.5px;
		opacity: 0.8;
		mix-blend-mode: difference;
		clip-path: polygon(
			0 0,
			calc(100% - var(--arrow)) 0,
			100% 50%,
			calc(100% - var(--arrow)) 100%,
			0 100%
		);
	}

	&__baseLeft,
	&__end {
		display: block;
		flex: 0 0 auto;
		width: auto; /* preserves proportions according to viewBox */
		height: 100%;
	}

	&__baseMid {
		display: block;
		flex: 1 1 auto; /* only this is stretched to width */
		min-width: 0;
		height: 100%;
	}

	&__end {
		margin-left: -0.125rem;
	}
}
</style>
